import { Link } from 'react-router-dom'

import styles from './Hero.module.css'

import { ReactComponent as Scroll } from '../../images/scroll.svg'
import { Text } from '../Text/Text'
import { BACKEND_URL } from '../../util'

import defaultImage from '../../images/photos/hero.jpeg'

type HeroProps = {
  title: string
  subtitle: string
  content: string
  image?: string
  overlayColor?: string
  overlayOpacity?: number
  showText?: boolean
  showButton?: boolean
  buttonText?: string
  buttonLink?: string
  textAlign?: 'left' | 'center' | 'right'
  textColor?: string
}

function overlayStyle(color: string = '#2a3e57', opacity: number = 78): string {
  const clamped = Math.max(0, Math.min(100, opacity))
  const alpha = Math.round((clamped / 100) * 255).toString(16).padStart(2, '0')
  return color + alpha
}

function Hero(props: HeroProps) {
  const imageUrl = props.image
    ? `${BACKEND_URL}file/${props.image}`
    : defaultImage

  const paragraphs = props.content
    ? props.content.split('\n').filter(line => line.trim() !== '')
    : []

  const buttonLink = props.buttonLink || '/wizard'
  const buttonText = props.buttonText || 'BENCHMARK & COMMISSION'
  const isExternal = buttonLink.startsWith('http')

  // Optional text-alignment control (#41). Aligns both the inner text and the
  // header/paragraph blocks (which are otherwise left-anchored at 55%/800px).
  const align = props.textAlign
  const blockAlign: React.CSSProperties | undefined =
    align === 'center' ? { marginLeft: 'auto', marginRight: 'auto' }
    : align === 'right' ? { marginLeft: 'auto', marginRight: 0 }
    : undefined

  // Optional CMS-picked text colour; falls back to the white set in the CSS module
  const textStyle: React.CSSProperties | undefined =
    props.textColor ? { color: props.textColor } : undefined

  return (
    <div className={styles.section} style={{ backgroundImage: `url(${imageUrl})` }}>
      <div
        className={styles.overlay}
        style={{ backgroundColor: overlayStyle(props.overlayColor, props.overlayOpacity) }}
      >
        <div className={styles.heroWrapper}>
          <div className={styles.container} style={align ? { textAlign: align } : undefined}>
            {props.showText !== false && (
              <>
                <div className={styles.header} style={blockAlign}>
                  <span className={styles.tagline} style={textStyle}>{props.subtitle}</span>
                  <h1 className={styles.title} style={textStyle}>{props.title}</h1>
                </div>
                {paragraphs.map((paragraph, index) => (
                  <Text key={index} className={styles.text} style={{ ...blockAlign, ...textStyle }}>{paragraph}</Text>
                ))}
              </>
            )}
            {props.showButton !== false && (
              <div className={styles.ctaWrapper}>
                {isExternal ? (
                  <a
                    className={styles.cta}
                    href={buttonLink}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {buttonText}
                  </a>
                ) : (
                  <Link className={styles.cta} to={buttonLink}>{buttonText}</Link>
                )}
              </div>
            )}
          </div>
          <div className={styles.scrollWrapper}>
            <Scroll className={styles.scroll}/>
          </div>
        </div>
      </div>
    </div>
  )
}

export { Hero }
